/**
 * How dark the shade over the sign-in picture has to be.
 *
 * The panel's text is light and sits over whatever the picture has in that
 * place. A fixed shade is either too heavy for a dark picture (it turns to
 * mud) or too light for a bright one (white birds, a sunny shed roof, and the
 * text disappears). So the shade is worked out from the picture: just dark
 * enough for each line of text to reach its contrast target over the
 * brightest part of the picture behind it, and tinted with the picture's own
 * colour so it reads as part of the picture rather than a grey film.
 */

/** What the web app measured on the picture when it was chosen. */
export interface PictureTone {
  /** `#rrggbb`: the picture's colour, darkened. */
  tint: string;
  /** Brightness (0-1) of the brightest parts of each third of the picture. */
  lumaTop: number;
  lumaMid: number;
  lumaBottom: number;
}

export interface Scrim {
  /** How much shade (0-1) at the top, middle and bottom of the panel. */
  top: number;
  mid: number;
  bottom: number;
  /** `r, g, b`, for use inside `rgba()`. */
  tint: string;
}

/**
 * The shade made for the built-in photograph, by hand: the default, and
 * unchanged from before pictures could be chosen.
 */
export const BUILT_IN_SCRIM: Scrim = { top: 0.78, mid: 0.6, bottom: 0.9, tint: '16, 18, 17' };

/** Below this the panel stops looking like the rest of the app. */
export const SCRIM_MIN = 0.35;
/** High contrast mode's own shade; never darker than that. */
export const SCRIM_MAX = 0.92;

/** The panel's text colours, from app.css (.login__tagline, .login__brandFoot p). */
const TAGLINE = '#c9ccc7';
const FOOT = '#d8dad6';
const WORDMARK = '#ffffff';

/** WCAG 2 relative luminance of an sRGB colour given as 0-255 channels. */
export function relativeLuminance(r: number, g: number, b: number): number {
  const linear = (c: number) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

export function contrast(a: number, b: number): number {
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

export function hexToRgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** The sRGB grey (0-255, unrounded) whose relative luminance is `luma`. */
function greyFor(luma: number): number {
  const s = luma <= 0.0031308 ? luma * 12.92 : 1.055 * luma ** (1 / 2.4) - 0.055;
  return Math.min(1, Math.max(0, s)) * 255;
}

/**
 * The least shade over a patch of brightness `luma` that lets `text` reach
 * `target`. Browsers blend in sRGB, channel by channel, so this does too. The
 * text's own shadow is left out: it helps, but is not counted on.
 */
export function shadeNeeded(
  luma: number,
  tint: [number, number, number],
  text: string,
  target: number,
): number {
  const textL = relativeLuminance(...hexToRgb(text));
  const grey = greyFor(luma);
  const over = (a: number) =>
    relativeLuminance(
      grey * (1 - a) + tint[0] * a,
      grey * (1 - a) + tint[1] * a,
      grey * (1 - a) + tint[2] * a,
    );
  if (contrast(textL, over(0)) >= target) return 0;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) / 2;
    if (contrast(textL, over(mid)) >= target) hi = mid;
    else lo = mid;
  }
  return hi;
}

/**
 * Aimed a little over each target: the bright patch is measured as one
 * brightness, but it is a colour, and blends very slightly differently.
 */
const MARGIN = 0.15;

const clamp = (v: number) => Math.min(SCRIM_MAX, Math.max(SCRIM_MIN, v));
/** Up, never down: rounding down could take the text just under its target. */
const round = (v: number) => Math.ceil(v * 100 - 1e-9) / 100;

/** The shade for a chosen picture, or the built-in one's when there is none. */
export function scrimFor(tone: PictureTone | null | undefined): Scrim {
  if (!tone) return BUILT_IN_SCRIM;
  const tint = /^#[0-9a-f]{6}$/i.test(tone.tint) ? hexToRgb(tone.tint) : [16, 18, 17];
  const rgb = tint as [number, number, number];
  const safe = (v: number) => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 1);

  // Top: the tagline is the smallest text there; the company name is large
  // text, which needs only 3:1. Bottom: the paragraph.
  const top = clamp(
    Math.max(
      shadeNeeded(safe(tone.lumaTop), rgb, TAGLINE, 4.5 + MARGIN),
      shadeNeeded(safe(tone.lumaTop), rgb, WORDMARK, 3 + MARGIN),
    ),
  );
  const bottom = clamp(shadeNeeded(safe(tone.lumaBottom), rgb, FOOT, 4.5 + MARGIN));
  // No text sits in the middle; a lighter band there lets the picture show,
  // but no lighter than its own brightness calls for, so the change from top
  // to bottom stays smooth over a bright middle.
  const mid = clamp(
    Math.max(
      Math.max(top, bottom) - 0.15,
      shadeNeeded(safe(tone.lumaMid), rgb, FOOT, 3),
    ),
  );
  return { top: round(top), mid: round(mid), bottom: round(bottom), tint: rgb.join(', ') };
}
